import { Controller } from "@hotwired/stimulus"

const WORKSPACE_MODE_STORAGE_PREFIX = "section-builder-workspace-mode"
const PREVIEW_VIEWPORT_STORAGE_PREFIX = "section-builder-preview-viewport"
const INSPECTOR_TAB_STORAGE_PREFIX = "section-builder-inspector-tab"
const DEFAULT_WORKSPACE_MODE = "canvas"
const DEFAULT_PREVIEW_VIEWPORT = "desktop"
const DEFAULT_INSPECTOR_TAB = "content"
const INSPECTOR_TABS = [ "content", "style", "advanced" ]
const DEFAULT_WIDGET_TAB = "all"
const WIDGET_TABS = [ "all", "frequent" ]
const DEFAULT_DIAGNOSTICS_FILTER = "all"
const DIAGNOSTICS_FILTERS = [ "all", "critical_high", "warnings" ]
const DIAGNOSTICS_STATE_KEY = "__sectionBuilderDiagnosticsState"
const DIAGNOSTIC_HIGHLIGHT_DURATION_MS = 1400
const PREVIEW_WIDTHS = {
  desktop: "100%",
  tablet: "1024px",
  mobile: "390px"
}

// Connects to data-controller="admin--section-workspace"
export default class extends Controller {
  static targets = [
    "patternsDrawer",
    "inspectorDrawer",
    "inspectorForm",
    "backdrop",
    "unsavedChangesDialog",
    "sectionCard",
    "elementCard",
    "patternsTab",
    "inspectorTab",
    "patternsTrigger",
    "inspectorTrigger",
    "inspectorTabButton",
    "inspectorTabPanel",
    "addWidgetModal",
    "addWidgetModalTitle",
    "addWidgetSlotInput",
    "addWidgetTabButton",
    "addWidgetTabPanel",
    "canvasPane",
    "previewPane",
    "previewFrame",
    "previewFrameShell",
    "viewModeButton",
    "viewportButton",
    "previewStatus",
    "diagnosticsModal",
    "diagnosticsTrigger",
    "diagnosticsFilterButton",
    "diagnosticsItem",
    "diagnosticsCount",
    "diagnosticsNoMatches",
    "sectionBody",
    "slotBody"
  ]

  static values = {
    selectedSectionId: Number,
    selectedElementId: Number,
    pageId: Number,
    previewUrl: String
  }

  connect() {
    this.workspaceMode = DEFAULT_WORKSPACE_MODE
    this.persistWorkspaceMode(DEFAULT_WORKSPACE_MODE)
    this.previewViewport = this.restorePreviewViewport()
    this.inspectorTab = this.restoreInspectorTab()
    this.widgetTab = DEFAULT_WIDGET_TAB
    this.diagnosticsFilter = this.restoreDiagnosticsFilter()
    this.previewDirty = true
    this.previewLoaded = false
    this.pendingInspectorCloseAction = null
    this.addWidgetModalTrigger = null
    this.resetInspectorDirtyState()

    this.previewLoadHandler = this.handlePreviewLoad.bind(this)
    if (this.hasPreviewFrameTarget) {
      this.previewFrameTarget.addEventListener("load", this.previewLoadHandler)
    }

    this.applyPreviewViewport()
    this.applyWorkspaceMode()
    this.applyInspectorTabs()
    this.applyAddWidgetTabs()
    this.applyDiagnosticsFilter()

    if (this.hasSelectedSectionIdValue || this.hasSelectedElementIdValue) {
      this.openInspector()
      this.applySelectionClasses()
    }
    this.updateBodyScrollLock()
  }

  disconnect() {
    if (this.diagnosticPulseTimeout) {
      clearTimeout(this.diagnosticPulseTimeout)
      this.diagnosticPulseTimeout = null
    }

    if (this.hasPreviewFrameTarget && this.previewLoadHandler) {
      this.previewFrameTarget.removeEventListener("load", this.previewLoadHandler)
    }
    this.closeUnsavedChangesDialog()
    document.body.classList.remove("overflow-hidden")
  }

  handleSubmitEnd(event) {
    const success = event?.detail?.success === true
    if (!success) return

    if (this.isInspectorEditForm(event?.target)) {
      this.captureInspectorSnapshot()
      if (this.consumeCloseInspectorOnSave(event.target)) {
        this.closeInspector()
      }
    }

    this.handleMutationSuccess()
  }

  inspectorFormTargetConnected() {
    this.captureInspectorSnapshot()
  }

  inspectorTabButtonTargetConnected() {
    this.applyInspectorTabs()
  }

  inspectorTabPanelTargetConnected() {
    this.applyInspectorTabs()
  }

  trackInspectorFormChange(event) {
    if (!this.inspectorOpen()) return

    const form = this.currentInspectorForm()
    if (!form || !form.contains(event.target)) return

    this.updateInspectorDirtyState()
  }

  handleMutationSuccess() {
    this.previewDirty = true
  }

  switchWorkspaceMode(event) {
    if (event) event.preventDefault()
    this.workspaceMode = DEFAULT_WORKSPACE_MODE
    this.persistWorkspaceMode(DEFAULT_WORKSPACE_MODE)
    this.applyWorkspaceMode()
  }

  switchPreviewViewport(event) {
    if (event) event.preventDefault()
    const viewport = event?.currentTarget?.dataset?.viewport
    if (!Object.prototype.hasOwnProperty.call(PREVIEW_WIDTHS, viewport)) return

    this.previewViewport = viewport
    this.persistPreviewViewport(viewport)
    this.applyPreviewViewport()
  }

  togglePatterns(event) {
    if (event) event.preventDefault()
    if (this.patternsOpen()) {
      this.closePatterns()
      return
    }

    if (!this.requestInspectorClose({ nextAction: "open_patterns" })) return
    this.openPatterns()
  }

  toggleInspector(event) {
    if (event) event.preventDefault()
    if (this.inspectorOpen()) {
      this.requestInspectorClose()
      return
    }

    this.closePatterns()
    this.openInspector()
  }

  closeAll(event) {
    if (!this.hasBackdropTarget) return
    if (event && event.target !== this.backdropTarget) return

    this.closePatterns()
    this.closeAddWidgetModal()
    this.closeDiagnosticsModal()
    this.requestInspectorClose()
  }

  handleEsc(event) {
    if (event.key !== "Escape") return

    if (this.addWidgetModalOpen()) {
      this.closeAddWidgetModal(event)
      return
    }

    if (this.diagnosticsModalOpen()) {
      this.closeDiagnosticsModal(event)
      return
    }

    if (this.unsavedChangesDialogOpen()) {
      this.cancelUnsavedInspectorChanges(event)
      return
    }

    this.closePatterns()
    this.requestInspectorClose()
  }

  selectSection(event) {
    const sectionId = Number(event.currentTarget.dataset.sectionId || 0)
    if (!sectionId) return

    this.selectedSectionIdValue = sectionId
    this.selectedElementIdValue = 0
    this.inspectorTab = DEFAULT_INSPECTOR_TAB
    this.persistInspectorTab(this.inspectorTab)
    this.applySelectionClasses()
    this.openInspector()
    this.closePatterns()
  }

  selectElement(event) {
    const sectionId = Number(event.currentTarget.dataset.sectionId || 0)
    const elementId = Number(event.currentTarget.dataset.elementId || 0)
    if (!sectionId || !elementId) return

    this.selectedSectionIdValue = sectionId
    this.selectedElementIdValue = elementId
    this.inspectorTab = DEFAULT_INSPECTOR_TAB
    this.persistInspectorTab(this.inspectorTab)
    this.applySelectionClasses()
    this.openInspector()
    this.closePatterns()
  }

  setDiagnosticsFilter(event) {
    if (event) event.preventDefault()
    const requested = event?.currentTarget?.dataset?.diagnosticsFilter
    this.diagnosticsFilter = this.normalizeDiagnosticsFilter(requested)
    this.persistDiagnosticsFilter(this.diagnosticsFilter)
    this.applyDiagnosticsFilter()
  }

  switchInspectorTab(event) {
    if (event) event.preventDefault()
    const requested = event?.currentTarget?.dataset?.inspectorTab
    this.inspectorTab = this.normalizeInspectorTab(requested)
    this.persistInspectorTab(this.inspectorTab)
    this.applyInspectorTabs()
  }

  openAddWidgetModal(event) {
    if (event) event.preventDefault()
    if (!this.hasAddWidgetModalTarget) return

    const source = event?.currentTarget
    const slotId = Number(source?.dataset?.slotId || 0)
    if (!slotId) return

    const slotLabel = source?.dataset?.slotLabel?.trim()
    if (this.hasAddWidgetModalTitleTarget) {
      this.addWidgetModalTitleTarget.textContent = slotLabel ? `Add widget to ${slotLabel}` : "Add widget"
    }
    this.addWidgetSlotInputTargets.forEach((input) => {
      input.value = String(slotId)
    })

    this.widgetTab = DEFAULT_WIDGET_TAB
    this.applyAddWidgetTabs()
    this.addWidgetModalTrigger = source instanceof HTMLElement ? source : null
    this.addWidgetModalTarget.classList.remove("hidden")
    this.addWidgetModalTarget.setAttribute("aria-hidden", "false")
    this.addWidgetModalTarget.focus()
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  closeAddWidgetModal(event) {
    if (event?.type === "keydown") event.preventDefault()
    if (!this.hasAddWidgetModalTarget) return

    if (this.addWidgetModalTarget.contains(document.activeElement)) {
      if (this.addWidgetModalTrigger && this.addWidgetModalTrigger.isConnected) {
        this.addWidgetModalTrigger.focus()
      } else if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }

    this.addWidgetModalTarget.classList.add("hidden")
    this.addWidgetModalTarget.setAttribute("aria-hidden", "true")
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  closeAddWidgetModalOnBackdrop(event) {
    if (!this.hasAddWidgetModalTarget) return
    if (event && event.target !== this.addWidgetModalTarget) return

    this.closeAddWidgetModal(event)
  }

  switchAddWidgetTab(event) {
    if (event) event.preventDefault()
    const requested = event?.currentTarget?.dataset?.widgetTab
    this.widgetTab = this.normalizeWidgetTab(requested)
    this.applyAddWidgetTabs()
  }

  openDiagnosticsModal(event) {
    if (event) event.preventDefault()
    if (!this.hasDiagnosticsModalTarget) return

    this.diagnosticsModalTarget.classList.remove("hidden")
    this.diagnosticsModalTarget.setAttribute("aria-hidden", "false")
    this.diagnosticsModalTarget.focus()
    this.applyDiagnosticsFilter()
    this.updateBodyScrollLock()
  }

  closeDiagnosticsModal(event) {
    if (event?.type === "keydown") event.preventDefault()
    if (!this.hasDiagnosticsModalTarget) return

    if (this.diagnosticsModalTarget.contains(document.activeElement)) {
      if (this.hasDiagnosticsTriggerTarget && this.diagnosticsTriggerTarget.isConnected) {
        this.diagnosticsTriggerTarget.focus()
      } else if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }

    this.diagnosticsModalTarget.classList.add("hidden")
    this.diagnosticsModalTarget.setAttribute("aria-hidden", "true")
    this.updateBodyScrollLock()
  }

  closeDiagnosticsModalOnBackdrop(event) {
    if (!this.hasDiagnosticsModalTarget) return
    if (event && event.target !== this.diagnosticsModalTarget) return

    this.closeDiagnosticsModal(event)
  }

  prepareDiagnosticNavigation(event) {
    const source = event?.currentTarget
    if (!source) return

    const sectionId = Number(source.dataset.diagnosticSectionId || 0)
    const slotId = Number(source.dataset.diagnosticSlotId || 0)
    const elementId = Number(source.dataset.diagnosticElementId || 0)
    if (!sectionId) return

    this.expandDiagnosticContainers({ sectionId, slotId })
    this.selectedSectionIdValue = sectionId
    this.selectedElementIdValue = elementId > 0 ? elementId : 0
    this.inspectorTab = DEFAULT_INSPECTOR_TAB
    this.persistInspectorTab(this.inspectorTab)
    this.applySelectionClasses()
    this.openInspector()
    this.closePatterns()
    this.closeDiagnosticsModal()

    const targetNode = this.resolveDiagnosticTargetNode({ sectionId, slotId, elementId })
    if (!targetNode) return

    window.requestAnimationFrame(() => {
      targetNode.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      targetNode.classList.add("section-builder-diagnostic-focus")
      if (this.diagnosticPulseTimeout) clearTimeout(this.diagnosticPulseTimeout)
      this.diagnosticPulseTimeout = setTimeout(() => {
        targetNode.classList.remove("section-builder-diagnostic-focus")
      }, DIAGNOSTIC_HIGHLIGHT_DURATION_MS)
    })
  }

  applyWorkspaceMode() {
    this.workspaceMode = DEFAULT_WORKSPACE_MODE

    if (this.hasCanvasPaneTarget) {
      this.canvasPaneTarget.classList.remove("hidden")
    }
    if (this.hasPreviewPaneTarget) {
      this.previewPaneTarget.classList.add("hidden")
    }

    this.viewModeButtonTargets.forEach((button) => {
      const active = button.dataset.mode === DEFAULT_WORKSPACE_MODE
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-primary-foreground", active)
      button.classList.toggle("text-muted-foreground", !active)
      button.classList.toggle("hover:text-card-foreground", !active)
      button.classList.toggle("bg-background", !active)
    })
  }

  applyPreviewViewport() {
    if (!this.hasPreviewFrameShellTarget) return

    const width = PREVIEW_WIDTHS[this.previewViewport] || PREVIEW_WIDTHS[DEFAULT_PREVIEW_VIEWPORT]
    this.previewFrameShellTarget.style.setProperty("--section-preview-frame-width", width)

    this.viewportButtonTargets.forEach((button) => {
      const active = button.dataset.viewport === this.previewViewport
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-primary-foreground", active)
      button.classList.toggle("text-muted-foreground", !active)
      button.classList.toggle("bg-background", !active)
    })
  }

  refreshPreview() {
    if (!this.hasPreviewFrameTarget || !this.hasPreviewUrlValue || !this.previewUrlValue) return

    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set("sb_preview_ts", `${Date.now()}`)

    this.previewFrameTarget.src = url.toString()
    this.previewDirty = false
    this.setPreviewStatus("Refreshing preview...")
  }

  handlePreviewLoad() {
    this.previewLoaded = true
    this.setPreviewStatus(`Updated ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`)
  }

  setPreviewStatus(message) {
    if (!this.hasPreviewStatusTarget) return
    this.previewStatusTarget.textContent = message
  }

  previewActive() {
    return this.workspaceMode === "preview"
  }

  openPatterns() {
    if (this.hasPatternsDrawerTarget) this.patternsDrawerTarget.classList.remove("-translate-x-full")
    this.toggleButtonState(this.patternsButtons(), true)
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  closePatterns() {
    if (this.hasPatternsDrawerTarget) this.patternsDrawerTarget.classList.add("-translate-x-full")
    this.toggleButtonState(this.patternsButtons(), false)
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  openInspector() {
    if (this.hasInspectorDrawerTarget) this.inspectorDrawerTarget.classList.remove("translate-x-full")
    this.toggleButtonState(this.inspectorButtons(), true)
    this.applyInspectorTabs()
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  closeInspector() {
    if (this.hasInspectorDrawerTarget) this.inspectorDrawerTarget.classList.add("translate-x-full")
    this.toggleButtonState(this.inspectorButtons(), false)
    this.resetInspectorDirtyState()
    this.updateBackdrop()
    this.updateBodyScrollLock()
  }

  patternsOpen() {
    return this.hasPatternsDrawerTarget && !this.patternsDrawerTarget.classList.contains("-translate-x-full")
  }

  inspectorOpen() {
    return this.hasInspectorDrawerTarget && !this.inspectorDrawerTarget.classList.contains("translate-x-full")
  }

  addWidgetModalOpen() {
    return this.hasAddWidgetModalTarget && !this.addWidgetModalTarget.classList.contains("hidden")
  }

  diagnosticsModalOpen() {
    return this.hasDiagnosticsModalTarget && !this.diagnosticsModalTarget.classList.contains("hidden")
  }

  updateBackdrop() {
    if (!this.hasBackdropTarget) return

    if (this.patternsOpen() || this.inspectorOpen()) {
      this.backdropTarget.classList.remove("hidden")
    } else {
      this.backdropTarget.classList.add("hidden")
    }
  }

  updateBodyScrollLock() {
    if (this.patternsOpen() || this.inspectorOpen() || this.addWidgetModalOpen() || this.diagnosticsModalOpen() || this.unsavedChangesDialogOpen()) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
  }

  applySelectionClasses() {
    const selectedSectionId = this.hasSelectedSectionIdValue ? this.selectedSectionIdValue : 0
    const selectedElementId = this.hasSelectedElementIdValue ? this.selectedElementIdValue : 0

    this.sectionCardTargets.forEach((card) => {
      const cardSectionId = Number(card.dataset.sectionId || 0)
      const directlySelected = selectedSectionId > 0 && cardSectionId === selectedSectionId && selectedElementId === 0

      card.classList.toggle("border-primary/40", directlySelected)
      card.classList.toggle("ring-1", directlySelected)
      card.classList.toggle("ring-primary/20", directlySelected)
      card.classList.toggle("border-border/80", !directlySelected)
    })

    this.elementCardTargets.forEach((card) => {
      const cardElementId = Number(card.dataset.elementId || 0)
      const selected = selectedElementId > 0 && cardElementId === selectedElementId
      card.classList.toggle("border-primary/45", selected)
      card.classList.toggle("ring-1", selected)
      card.classList.toggle("ring-primary/25", selected)
      card.classList.toggle("border-border/80", !selected)
    })
  }

  applyDiagnosticsFilter() {
    const filter = this.normalizeDiagnosticsFilter(this.diagnosticsFilter)
    let visibleCount = 0

    this.diagnosticsItemTargets.forEach((item) => {
      const bucket = item.dataset.diagnosticsFilterBucket || "warnings"
      const visible = filter === "all" || bucket === filter
      item.classList.toggle("hidden", !visible)
      if (visible) visibleCount += 1
    })

    this.diagnosticsFilterButtonTargets.forEach((button) => {
      const active = button.dataset.diagnosticsFilter === filter
      button.setAttribute("aria-pressed", active ? "true" : "false")
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-primary-foreground", active)
      button.classList.toggle("border-primary/40", active)
      button.classList.toggle("text-muted-foreground", !active)
      button.classList.toggle("border-border", !active)
    })

    if (this.hasDiagnosticsCountTarget) {
      this.diagnosticsCountTarget.textContent = `${visibleCount} visible`
    }

    if (this.hasDiagnosticsNoMatchesTarget) {
      const shouldHideNoMatches = visibleCount > 0 || this.diagnosticsItemTargets.length === 0
      this.diagnosticsNoMatchesTarget.classList.toggle("hidden", shouldHideNoMatches)
    }
  }

  toggleButtonState(buttons, active) {
    buttons.forEach((button) => {
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-primary-foreground", active)
      button.classList.toggle("text-foreground", !active)
    })
  }

  patternsButtons() {
    return [ ...(this.hasPatternsTabTarget ? [ this.patternsTabTarget ] : []), ...this.patternsTriggerTargets ]
  }

  inspectorButtons() {
    return [ ...(this.hasInspectorTabTarget ? [ this.inspectorTabTarget ] : []), ...this.inspectorTriggerTargets ]
  }

  restoreWorkspaceMode() {
    const persisted = this.readStorage(this.workspaceModeStorageKey())
    return ["canvas", "preview"].includes(persisted) ? persisted : DEFAULT_WORKSPACE_MODE
  }

  persistWorkspaceMode(mode) {
    this.writeStorage(this.workspaceModeStorageKey(), mode)
  }

  restorePreviewViewport() {
    const persisted = this.readStorage(this.previewViewportStorageKey())
    return Object.prototype.hasOwnProperty.call(PREVIEW_WIDTHS, persisted) ? persisted : DEFAULT_PREVIEW_VIEWPORT
  }

  persistPreviewViewport(viewport) {
    this.writeStorage(this.previewViewportStorageKey(), viewport)
  }

  restoreDiagnosticsFilter() {
    const state = this.readDiagnosticsState()
    return this.normalizeDiagnosticsFilter(state?.filter)
  }

  restoreInspectorTab() {
    const persisted = this.readStorage(this.inspectorTabStorageKey())
    return this.normalizeInspectorTab(persisted)
  }

  persistDiagnosticsFilter(filter) {
    this.writeDiagnosticsState({ filter: this.normalizeDiagnosticsFilter(filter) })
  }

  persistInspectorTab(tab) {
    this.writeStorage(this.inspectorTabStorageKey(), this.normalizeInspectorTab(tab))
  }

  normalizeDiagnosticsFilter(value) {
    return DIAGNOSTICS_FILTERS.includes(value) ? value : DEFAULT_DIAGNOSTICS_FILTER
  }

  normalizeInspectorTab(value) {
    return INSPECTOR_TABS.includes(value) ? value : DEFAULT_INSPECTOR_TAB
  }

  normalizeWidgetTab(value) {
    return WIDGET_TABS.includes(value) ? value : DEFAULT_WIDGET_TAB
  }

  applyInspectorTabs() {
    const activeTab = this.normalizeInspectorTab(this.inspectorTab)
    this.inspectorTab = activeTab

    this.inspectorTabButtonTargets.forEach((button) => {
      const buttonTab = this.normalizeInspectorTab(button.dataset.inspectorTab)
      const active = buttonTab === activeTab

      button.setAttribute("aria-selected", active ? "true" : "false")
      button.classList.toggle("bg-foreground", active)
      button.classList.toggle("text-background", active)
      button.classList.toggle("border-foreground", active)
      button.classList.toggle("text-muted-foreground", !active)
      button.classList.toggle("bg-background", !active)
      button.classList.toggle("border-border", !active)
    })

    this.inspectorTabPanelTargets.forEach((panel) => {
      const panelTab = this.normalizeInspectorTab(panel.dataset.inspectorTabPanel)
      panel.classList.toggle("hidden", panelTab !== activeTab)
    })
  }

  applyAddWidgetTabs() {
    const activeTab = this.normalizeWidgetTab(this.widgetTab)
    this.widgetTab = activeTab

    this.addWidgetTabButtonTargets.forEach((button) => {
      const buttonTab = this.normalizeWidgetTab(button.dataset.widgetTab)
      const active = buttonTab === activeTab
      button.setAttribute("aria-pressed", active ? "true" : "false")
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-primary-foreground", active)
      button.classList.toggle("text-muted-foreground", !active)
      button.classList.toggle("bg-background", !active)
    })

    this.addWidgetTabPanelTargets.forEach((panel) => {
      const panelTab = this.normalizeWidgetTab(panel.dataset.widgetTabPanel)
      panel.classList.toggle("hidden", panelTab !== activeTab)
    })
  }

  workspaceModeStorageKey() {
    return `${WORKSPACE_MODE_STORAGE_PREFIX}:${this.pageStorageKey()}`
  }

  previewViewportStorageKey() {
    return `${PREVIEW_VIEWPORT_STORAGE_PREFIX}:${this.pageStorageKey()}`
  }

  inspectorTabStorageKey() {
    return `${INSPECTOR_TAB_STORAGE_PREFIX}:${this.pageStorageKey()}`
  }

  pageStorageKey() {
    if (!this.hasPageIdValue || !this.pageIdValue) return "global"
    return String(this.pageIdValue)
  }

  readStorage(key) {
    try {
      return window.localStorage.getItem(key)
    } catch (_error) {
      return null
    }
  }

  writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch (_error) {
      // ignore storage failures (private mode / restricted context)
    }
  }

  readDiagnosticsState() {
    const pageId = this.pageStorageKey()
    const state = window[DIAGNOSTICS_STATE_KEY]
    if (!state || state.pageId !== pageId) return null

    return state
  }

  writeDiagnosticsState(data) {
    const pageId = this.pageStorageKey()
    const existing = this.readDiagnosticsState() || {}
    window[DIAGNOSTICS_STATE_KEY] = {
      ...existing,
      ...data,
      pageId
    }
  }

  expandDiagnosticContainers({ sectionId, slotId }) {
    const sectionBody = this.findSectionBody(sectionId)
    if (sectionBody) sectionBody.classList.remove("hidden")

    const slotBody = this.findSlotBody(slotId)
    if (slotBody) slotBody.classList.remove("hidden")
  }

  findSectionBody(sectionId) {
    if (!sectionId || !this.hasSectionBodyTarget) return null

    return this.sectionBodyTargets.find((body) => Number(body.dataset.sectionId || 0) === Number(sectionId)) || null
  }

  findSlotBody(slotId) {
    if (!slotId || !this.hasSlotBodyTarget) return null

    return this.slotBodyTargets.find((body) => Number(body.dataset.slotId || 0) === Number(slotId)) || null
  }

  resolveDiagnosticTargetNode({ sectionId, slotId, elementId }) {
    if (elementId > 0) {
      const elementCard = this.element.querySelector(`[data-element-id="${elementId}"]`)
      if (elementCard) return elementCard
    }

    if (slotId > 0) {
      const slotContainer = this.element.querySelector(`[data-slot-id="${slotId}"]`)
      if (slotContainer) return slotContainer
    }

    if (sectionId > 0) {
      const sectionCard = this.element.querySelector(`[data-section-id="${sectionId}"]`)
      if (sectionCard) return sectionCard
    }

    return null
  }

  requestInspectorClose({ nextAction = null } = {}) {
    if (!this.inspectorOpen()) return true

    if (!this.currentInspectorDirty()) {
      this.closeInspector()
      if (nextAction === "open_patterns") this.openPatterns()
      return true
    }

    this.pendingInspectorCloseAction = nextAction
    this.openUnsavedChangesDialog()
    return false
  }

  saveUnsavedInspectorChanges(event) {
    if (event) event.preventDefault()

    const form = this.currentInspectorForm()
    if (!form) {
      this.discardUnsavedInspectorChanges()
      return
    }

    if (typeof form.reportValidity === "function" && !form.reportValidity()) return

    this.applyCloseInspectorOnSave(form)
    this.pendingInspectorCloseAction = null
    this.closeUnsavedChangesDialog()
    form.requestSubmit()
  }

  discardUnsavedInspectorChanges(event) {
    if (event) event.preventDefault()

    const nextAction = this.pendingInspectorCloseAction
    this.pendingInspectorCloseAction = null

    this.closeUnsavedChangesDialog()
    this.closeInspector()
    if (nextAction === "open_patterns") this.openPatterns()
  }

  cancelUnsavedInspectorChanges(event) {
    if (event) event.preventDefault()

    this.pendingInspectorCloseAction = null
    this.closeUnsavedChangesDialog()
    this.updateBodyScrollLock()
  }

  swallowEvent(event) {
    if (!event) return
    event.stopPropagation()
  }

  openUnsavedChangesDialog() {
    if (!this.hasUnsavedChangesDialogTarget) return

    this.unsavedChangesDialogTarget.classList.remove("hidden")
    const primaryActionButton = this.unsavedChangesDialogTarget.querySelector("[data-unsaved-action='save']")
    if (primaryActionButton) primaryActionButton.focus()
    this.updateBodyScrollLock()
  }

  closeUnsavedChangesDialog() {
    if (!this.hasUnsavedChangesDialogTarget) return

    this.unsavedChangesDialogTarget.classList.add("hidden")
    this.updateBodyScrollLock()
  }

  unsavedChangesDialogOpen() {
    return this.hasUnsavedChangesDialogTarget && !this.unsavedChangesDialogTarget.classList.contains("hidden")
  }

  currentInspectorDirty() {
    return this.updateInspectorDirtyState()
  }

  updateInspectorDirtyState() {
    const form = this.currentInspectorForm()
    if (!form) {
      this.resetInspectorDirtyState()
      return false
    }

    const snapshotKey = this.inspectorSnapshotKeyFor(form)
    if (this.inspectorSnapshot == null || this.inspectorSnapshotKey !== snapshotKey) {
      this.captureInspectorSnapshot()
    }

    this.inspectorDirty = this.serializeInspectorForm(form) !== this.inspectorSnapshot
    return this.inspectorDirty
  }

  captureInspectorSnapshot() {
    const form = this.currentInspectorForm()
    if (!form) {
      this.resetInspectorDirtyState()
      return
    }

    this.inspectorSnapshotKey = this.inspectorSnapshotKeyFor(form)
    this.inspectorSnapshot = this.serializeInspectorForm(form)
    this.inspectorDirty = false
  }

  resetInspectorDirtyState() {
    this.inspectorDirty = false
    this.inspectorSnapshot = null
    this.inspectorSnapshotKey = null
  }

  currentInspectorForm() {
    if (!this.hasInspectorFormTarget) return null
    return this.inspectorFormTarget
  }

  isInspectorEditForm(form) {
    if (!(form instanceof HTMLFormElement)) return false
    return form.dataset.sectionWorkspaceInspectorForm === "true"
  }

  inspectorSnapshotKeyFor(form) {
    const action = form.getAttribute("action") || ""
    const methodOverride = form.querySelector("input[name='_method']")?.value || form.getAttribute("method") || "get"
    return `${action}|${String(methodOverride).toUpperCase()}`
  }

  serializeInspectorForm(form) {
    const payload = []
    const formData = new FormData(form)

    formData.forEach((value, key) => {
      if (key === "close_inspector_on_success") return

      let normalizedValue
      if (value instanceof File) {
        const emptyUpload = value.name === "" && value.size === 0
        normalizedValue = emptyUpload ? "" : `file:${value.name}:${value.size}:${value.type}:${value.lastModified}`
      } else {
        normalizedValue = String(value)
      }
      payload.push([ key, normalizedValue ])
    })

    payload.sort((left, right) => {
      if (left[0] === right[0]) return left[1].localeCompare(right[1])
      return left[0].localeCompare(right[0])
    })

    return JSON.stringify(payload)
  }

  applyCloseInspectorOnSave(form) {
    let marker = form.querySelector("input[name='close_inspector_on_success']")
    if (!marker) {
      marker = document.createElement("input")
      marker.type = "hidden"
      marker.name = "close_inspector_on_success"
      form.appendChild(marker)
    }

    marker.value = "1"
  }

  consumeCloseInspectorOnSave(form) {
    if (!(form instanceof HTMLFormElement)) return false

    const marker = form.querySelector("input[name='close_inspector_on_success']")
    if (!marker) return false

    const shouldClose = marker.value === "1"
    marker.remove()
    return shouldClose
  }
}
